import {
  type Dinero,
  type DineroCurrency,
  dinero,
  multiply,
  toDecimal,
} from "dinero.js";
import * as Currencies from "@dinero.js/currencies";

interface DineroFromFloatOptions {
  amount: number;
  currency: DineroCurrency<number>;
  scale?: number;
}

/**
 * Converts a floating-point number to a Dinero object.
 *
 * @param options - The options for creating the Dinero object.
 * @param options.amount - The float amount to convert.
 * @param options.currency - The currency object.
 * @param options.scale - Optional custom scale (precision) for the amount.
 * @returns A Dinero object representing the input amount.
 * @throws Error if the amount is not finite or if the calculation results in a non-integer amount.
 */
export function dineroFromFloat({
  amount,
  currency,
  scale,
}: DineroFromFloatOptions): Dinero<number> {
  if (!Number.isFinite(amount)) {
    throw new Error("Amount must be a finite number");
  }

  const base = Array.isArray(currency.base) ? currency.base[0] : currency.base;
  const factor = Math.pow(base, currency.exponent);
  const effectiveFactor = factor || scale || 1;
  const roundedAmount = Math.round(amount * effectiveFactor);

  if (!Number.isInteger(roundedAmount)) {
    throw new Error("Calculation resulted in a non-integer amount");
  }

  return dinero({ amount: roundedAmount, currency, scale });
}

export function dineroSubUnit({ currency }: { currency: string }) {
  const selectedCurrency = Currencies[currency as keyof typeof Currencies];
  if (!selectedCurrency) {
    throw new Error(`Unsupported currency type: ${currency}`);
  }
  return Math.pow(10, selectedCurrency.exponent);
}

function getCurrency({
  currency = "SAR",
}: {
  currency: string;
}): DineroCurrency<number> {
  const result = Currencies[currency as keyof typeof Currencies];
  if (!result) {
    throw new Error(`Unsupported currency type: ${currency}`);
  }
  return result;
}

// Creates a Dinero object for the subtotal in subUnit
// TODO: to be deleted later
export function createSubtotal({
  subTotal,
  subUnit,
  currency = "SAR",
}: {
  subTotal: number;
  subUnit: number;
  currency: string;
}) {
  // Convert subTotal to a Dinero object
  const dineroSubTotal = dinero({
    amount: subTotal * subUnit,
    currency: getCurrency({ currency }),
  });

  // Multiply the Dinero object by subUnit
  let value: any = multiply(dineroSubTotal, { amount: subUnit });
  value = toDecimal(value);
  value = parseFloat(value);
  return value;
}

// TODO: to be deleted later
export function calculateTaxBySubtotal({
  subTotal,
  subUnit,
  taxRate,
  currency = "SAR",
}: {
  subTotal: number;
  subUnit: number;
  taxRate: number;
  currency: string;
}) {
  // Create a Dinero object for subTotal
  const subTotalDinero = dinero({
    amount: subTotal * subUnit,
    currency: getCurrency({ currency }),
  });
  let subTotalValue: any = multiply(subTotalDinero, { amount: subUnit });
  const taxRateDinero = { amount: taxRate, scale: 2 };
  // Multiply subTotal by taxRate
  let tax: any = multiply(subTotalValue, taxRateDinero);
  tax = toDecimal(tax);
  tax = parseFloat(tax);
  return tax;
}

const toFixedWithoutRounding = (
  amount: number,
  subUnit: number,
  decimals: number
): string => {
  const factor = Math.pow(10, decimals);
  const result = amount / subUnit;
  const truncatedResult = Math.floor(result * factor) / factor;

  // Return the truncated result as a string with specified decimals without rounding
  return truncatedResult.toFixed(decimals);
};

export function numberToCurrency(
  amount: number,
  currency: string,
  decimals: number
): number {
  let subUnit = dineroSubUnit({ currency: currency || "SAR" });
  if (!subUnit || subUnit <= 0) {
    throw new Error("Invalid subUnit provided. Must be greater than 0.");
  }
  let value = toFixedWithoutRounding(amount, subUnit, decimals);

  return JSON.parse(value);
}
